import { useCallback, useMemo, useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { putJSON } from 'api/fetch';
import Icon from 'fe-design-base/atoms/Icon';
import FDBText from 'fe-design-base/atoms/Text';
import Alert from 'fe-design-base/molecules/Alert';
import Pill from 'fe-design-base/molecules/Pill/Pill';
import Tooltip from 'fe-design-base/molecules/Tooltip';
import { fromJS } from 'immutable';
import { partial } from 'lodash';
import PropTypes from 'prop-types';

import {
  getCurrentCompany,
  getCurrentUserIsPayrollAdmin,
  getHasPayrollEnabled,
  getPayrollAddressValidationRollout,
} from 'selectors/session';

import {
  einPublicKey,
  ssnPublicKey,
} from 'features/employeeOnboarding/constants';

import Box from 'components/Box';
import { isInUS } from 'components/forms/TextField/util';
import ITINError from 'components/ITINError';
import Text, { TeamSectionTitle, Warning } from 'components/Text';

import { cxHelpers } from 'util/className';
import { EIN_REGEX, SSN_REGEX } from 'util/constants';
import { encryptWithPublicKey } from 'util/encryption';
import { error as flashError } from 'util/flashNotice';
import { toI18n } from 'util/i18n';
import { pick } from 'util/objectMethods';
import { isValidEin, isValidSSN, zipValidator } from 'util/validators';

import RequiredAttribute from '../../components/RequiredAttribute';
import EditableSection from '../../EditableSection';
import FieldRenderer from '../../FieldRenderer';
import { isValidDate } from '../../util';

import CheckAddressError from './CheckAddressError';
import CheckAddressSuggestion from './CheckAddressSuggestion';
import { CHECK_ADDRESS_ERROR, PERSONAL_INFORMATION_FIELDS } from './constants';
import EmployeeInformationForm from './EmployeeInformationForm';
import TempUserToolTip from './TempUserToolTip';
import { getInitialErrors } from './utils';

const { cx, cxEl } = cxHelpers('EmployeeInformationWidget');

const zipIsInvalid = zipValidator('Zip/Postal Code');
const ssnIsInvalid = isValidSSN();
const einIsInvalid = isValidEin();

const anyAddressFields = values =>
  values.address_id ||
  values.address_1 ||
  values.address_2 ||
  values.city ||
  values.state ||
  values.zip_code;

const anyContractorInfoFields = values =>
  values.contractor_info_id ||
  values.contractor_info_business_name ||
  values.contractor_info_decrypted_ein;

const EmployeeInformationWidget = ({
  user,
  updateUserInfo,
  openByDefault,
  mobile,
  isGodModing,
  stateOptions,
  currentCompany,
  hasPayrollEnabled,
  currentUserIsPayrollAdmin,
  teamDrawerMode,
  initEditMode,
  onSaveCompleted,
  onSsnValidationError,
  payrollAddressValidationRollout,
  disableEdits,
  showEmployeeWillBeNotifiedAlert,
  employeeInformationEditButton,
  showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert,
}) => {
  const hasW2Jobs = user.get('has_w2_jobs');
  const renderComma = field => (!!field ? ',' : '');

  // We want to simply use a text field without a mask if the location of the person
  // is outside of the United States
  const zipMask = () => (isInUS() ? 'zip' : null);

  const isTempUser = user.get('is_temp_user');

  const [suggestedAddress, setSuggestedAddress] = useState({});

  const [isSSNChanged, setIsSSNChanged] = useState(false);
  const [isSSNInvalid, setIsSSNInvalid] = useState(
    user.get('invalid_ssn_validation_status?')
  );
  const [isSSNVisible, setIsSSNVisible] = useState(false);
  const handleSSNChange = useCallback(() => {
    setIsSSNChanged(true);
  }, []);
  const handleClickSSNVisible = useCallback(e => {
    e.preventDefault();
    setIsSSNVisible(currState => !currState);
  }, []);

  const clearEINValue = user_data => {
    user_data.contractor_info_decrypted_ein = null;
    return user_data;
  };

  const clearSSNValue = user_data => {
    user_data.decrypted_ssn = null;
    return user_data;
  };

  const handleOnBlurSsn = useCallback((setFieldValue, values) => {
    if (!values.decrypted_ssn) {
      setIsSSNChanged(false);
      setFieldValue('decrypted_ssn', null);
    }
  }, []);

  const initialValues = useMemo(() => {
    let user_data;
    user_data = pick(user.toJS(), PERSONAL_INFORMATION_FIELDS);

    user_data = clearEINValue(user_data);

    user_data = clearSSNValue(user_data);

    return user_data;
  }, [user]);

  const clearFormData = useCallback(
    values => {
      setIsSSNChanged(false);
      if (payrollAddressValidationRollout) setSuggestedAddress({});
      clearEINValue(values);
      clearSSNValue(values);
    },
    [payrollAddressValidationRollout]
  );

  const handleITINValidation = useCallback(
    ssn => hasW2Jobs && ssn?.startsWith('9', 0),
    [hasW2Jobs]
  );

  const handleValidate = useCallback(
    values => {
      const errors = {};

      if (!isTempUser) {
        if (!values.zip_code) {
          errors.zip_code = toI18n(
            'team.employee_profile.payroll_ee_information.validations.required_field'
          );
        }

        if (hasPayrollEnabled && user.get('included_in_payroll')) {
          if (!values.legal_first_name) {
            errors.legal_first_name = toI18n(
              'team.employee_profile.payroll_ee_information.validations.required_field'
            );
          }

          if (!values.legal_last_name) {
            errors.legal_last_name = toI18n(
              'team.employee_profile.payroll_ee_information.validations.required_field'
            );
          }

          if (
            (user.get('tax_classification') === 'w2' ||
              user.get('contractor_type') === 'individual') &&
            !values.decrypted_ssn &&
            !user.get('decrypted_ssn')
          ) {
            errors.decrypted_ssn = toI18n(
              'team.employee_profile.payroll_ee_information.validations.required_field'
            );
          }

          if (!values.address_1) {
            errors.address_1 = toI18n(
              'team.employee_profile.payroll_ee_information.validations.required_field'
            );
          }

          if (!values.state) {
            errors.state = toI18n(
              'team.employee_profile.payroll_ee_information.validations.required_field'
            );
          }

          if (!values.city) {
            errors.city = toI18n(
              'team.employee_profile.payroll_ee_information.validations.required_field'
            );
          }

          if (
            user.get('tax_classification') === 'w2' &&
            !values.date_of_birth
          ) {
            errors.date_of_birth = toI18n(
              'team.employee_profile.payroll_ee_information.validations.required_field'
            );
          }
        }
      }

      if (
        isSSNChanged &&
        values.decrypted_ssn &&
        ssnIsInvalid(values.decrypted_ssn)
      ) {
        errors.decrypted_ssn = toI18n(
          'team.employee_profile.employee_information.validations.ssn'
        );
      }

      if (handleITINValidation(values.decrypted_ssn)) {
        errors.decrypted_ssn = <ITINError />;
      }

      if (
        user.get('tax_classification') === '1099' &&
        user.get('contractor_type') === 'business' &&
        (!values.contractor_info_decrypted_ein ||
          einIsInvalid(values.contractor_info_decrypted_ein))
      ) {
        errors.contractor_info_decrypted_ein = toI18n(
          'team.employee_profile.employee_information.validations.ein'
        );
      }
      if (!isValidDate(values.date_of_birth)) {
        errors.date_of_birth = toI18n(
          'team.employee_profile.employee_information.validations.date_of_birth'
        );
      }
      if (values.zip_code && zipIsInvalid(values.zip_code) && isInUS()) {
        errors.zip_code = toI18n(
          'team.employee_profile.employee_information.validations.zip_code.format'
        );
      }

      return errors;
    },
    [isTempUser, isSSNChanged, user, hasPayrollEnabled, handleITINValidation]
  );

  const showIsRemoteReadMode = user.get('show_is_remote_read_mode');
  const showIsRemoteEditMode = user.get('show_is_remote_edit_mode');
  const showIsRemoteField =
    user.get('show_is_remote_field') &&
    user.get('tax_classification') !== '1099';
  const isRemoteTeamMemberReadModeValue = user.get(
    'is_remote_team_member_read_mode_value'
  );
  const userIncludedInProcessingPayrollRun = user.get(
    'user_included_in_processing_payroll_run'
  );

  const [showIsRemoteAlert, setShowIsRemoteAlert] = useState(false);

  const normalizeString = useCallback(str => str?.toLowerCase()?.trim(), []);

  const isAddressValueChanged = useCallback(
    (value, field_name) =>
      user.get(field_name) &&
      normalizeString(value) !== normalizeString(user.get(field_name)),
    [user, normalizeString]
  );

  const handleShowRemoteTeamMemberAlert = useCallback(
    values => {
      const dataChanged =
        values.is_remote !== user.get('is_remote') ||
        (values.is_remote &&
          user.get('is_remote') &&
          (isAddressValueChanged(values.city, 'city') ||
            isAddressValueChanged(values.state, 'state')));
      setShowIsRemoteAlert(userIncludedInProcessingPayrollRun && dataChanged);
    },
    [isAddressValueChanged, userIncludedInProcessingPayrollRun, user]
  );

  const handleSave = useCallback(
    values => {
      const payload = {
        legal_first_name: values.legal_first_name,
        middle_name: values.middle_name,
        legal_last_name: values.legal_last_name,
        date_of_birth: values.date_of_birth,
        encrypted_ssn: SSN_REGEX.test(values.decrypted_ssn)
          ? encryptWithPublicKey(ssnPublicKey(), values.decrypted_ssn) || null
          : null,

        address_attributes: anyAddressFields(values)
          ? {
              id: values.address_id,
              address_1: values.address_1,
              address_2: values.address_2,
              city: values.city,
              state: values.state,
              zip: isInUS() ? values.zip_code.substr(0, 5) : values.zip_code,
              current_company_id: currentCompany.get('id'),
            }
          : undefined,

        contractor_info_attributes: anyContractorInfoFields(values)
          ? {
              id: values.contractor_info_id,
              business_name: values.contractor_info_business_name,
              encrypted_ein: EIN_REGEX.test(
                values.contractor_info_decrypted_ein
              )
                ? encryptWithPublicKey(
                    einPublicKey(),
                    // remove the EIN dash before sending the EIN to the BE
                    values.contractor_info_decrypted_ein.replace('-', '')
                  ) || null
                : null,
            }
          : undefined,
        is_payroll_info: true,
        company_users_attributes: { is_remote: values.is_remote },
      };

      clearFormData(values);
      onSsnValidationError(false);
      setIsSSNInvalid(false);

      const res = putJSON(
        `/employees/${user.get('id')}/update_personal_information.json`,
        {
          personal_information_params: payload,
        }
      );

      return res;
    },
    [currentCompany, clearFormData, user, onSsnValidationError, setIsSSNInvalid]
  );

  const handleOnError = useCallback(
    error => {
      error.response.json().then(data => {
        if (payrollAddressValidationRollout)
          setSuggestedAddress(data?.address_check_error);
        if (data?.error === 'ssn_validation_error') {
          onSsnValidationError(true);
        } else if (data?.message) {
          const errorValues = Object.values(data.message);
          const uniqueErrors = [...new Set(errorValues.flat())];
          if (typeof uniqueErrors[0] === 'string') {
            flashError(uniqueErrors.join('. '));
          } else if (uniqueErrors[0].full_messages) {
            flashError(uniqueErrors[0].full_messages.join('. '));
          } else {
            flashError(toI18n('errors.generic'));
          }
        } else {
          flashError(toI18n('errors.generic'));
        }
      });
    },
    [onSsnValidationError, payrollAddressValidationRollout]
  );

  const isTooltipEnabled = useMemo(
    () => disableEdits && !showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert,
    [disableEdits, showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert]
  );

  const isFieldDisabled = useMemo(
    () =>
      showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert && isGodModing
        ? false
        : disableEdits,
    [
      disableEdits,
      showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert,
      isGodModing,
    ]
  );

  const renderLegalName = (editMode, errors) => (
    <Box className={cxEl('legal-name')} mt={0}>
      {editMode ? (
        <Box>
          <Box vcenter spacebetween>
            <Box w={mobile ? '40%' : '20%'}>
              <TeamSectionTitle i18n="team.employee_profile.employee_information.legal_first_name" />
            </Box>
            <Box
              w={mobile ? '60%' : '80%'}
              className={cxEl('legal-first-name')}
            >
              <Box w={mobile ? '100%' : '48%'}>
                <TempUserToolTip
                  name={user.get('first_name')}
                  enabled={isTooltipEnabled}
                >
                  <FieldRenderer
                    editMode
                    name="legal_first_name"
                    component="TextField"
                    theme="bordered"
                    errorBorder={errors.legal_first_name}
                    debounced={false}
                    disabled={isFieldDisabled}
                    placeholder={toI18n(
                      'team.employee_profile.employee_information.placeholders.first_name'
                    )}
                  />
                </TempUserToolTip>
              </Box>
            </Box>
          </Box>
          <Box vcenter spacebetween mt12>
            <Box w={mobile ? '40%' : '20%'}>
              <TeamSectionTitle i18n="team.employee_profile.employee_information.legal_middle_name" />
            </Box>
            <Box className={cxEl('middle-name')} w={mobile ? '60%' : '80%'}>
              <Box w={mobile ? '100%' : '48%'}>
                <TempUserToolTip
                  name={user.get('first_name')}
                  enabled={isTooltipEnabled}
                >
                  <FieldRenderer
                    editMode
                    name="middle_name"
                    component="TextField"
                    theme="bordered"
                    debounced={false}
                    disabled={isFieldDisabled}
                    placeholder={toI18n(
                      'team.employee_profile.employee_information.placeholders.middle_name'
                    )}
                  />
                </TempUserToolTip>
              </Box>
            </Box>
          </Box>
          <Box vcenter spacebetween mt12>
            <Box w={mobile ? '40%' : '20%'}>
              <TeamSectionTitle i18n="team.employee_profile.employee_information.legal_last_name" />
            </Box>
            <Box className={cxEl('legal-last-name')} w={mobile ? '60%' : '80%'}>
              <Box w={mobile ? '100%' : '48%'}>
                <TempUserToolTip
                  name={user.get('first_name')}
                  enabled={isTooltipEnabled}
                >
                  <FieldRenderer
                    editMode
                    name="legal_last_name"
                    component="TextField"
                    theme="bordered"
                    errorBorder={errors.legal_last_name}
                    debounced={false}
                    placeholder={
                      mobile
                        ? null
                        : toI18n(
                            'team.employee_profile.employee_information.placeholders.last_name'
                          )
                    }
                    disabled={isFieldDisabled}
                  />
                </TempUserToolTip>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box row>
          <Box w={mobile ? '40%' : '20%'}>
            <TeamSectionTitle i18n="team.employee_profile.employee_information.legal_name" />
          </Box>
          <Box vcenter={mobile} w={mobile ? '60%' : '80%'}>
            <RequiredAttribute
              user={user}
              targetObject={user}
              attribute="legal_first_name"
            >
              <Text navy>{`${user.get('legal_first_name') || ''} ${
                user.get('middle_name') || ''
              } ${user.get('legal_last_name') || ''}`}</Text>
            </RequiredAttribute>
          </Box>
        </Box>
      )}
    </Box>
  );

  const renderIsRemoteCheckBox = editMode => (
    <Box className={cxEl('is_remote')} mt={12}>
      {editMode
        ? showIsRemoteEditMode && (
            <Box>
              <Box vcenter>
                <Box className={cxEl('is_remote')}>
                  <Box row vcenter>
                    <TempUserToolTip
                      name={user.get('first_name')}
                      enabled={isTooltipEnabled}
                    >
                      <FieldRenderer
                        editMode
                        name="is_remote"
                        component="CheckboxInput"
                        debounced={false}
                        disabled={isFieldDisabled}
                        color="primary"
                        label={
                          <Box row vcenter>
                            <FDBText
                              variant="body"
                              i18n="team.employee_profile.payroll_ee_information.is_remote.edit_mode"
                              i18nProps={{
                                name:
                                  user.get('legal_name') || user.get('name'),
                              }}
                              asHTML
                            />
                            <Tooltip
                              content={
                                <FDBText
                                  variant="bodySm"
                                  i18n="team.employee_profile.payroll_ee_information.tooltips.is_remote"
                                />
                              }
                              placement="top"
                            >
                              <Icon
                                ml={8}
                                iconName="Info"
                                size="medium"
                                color="info300"
                              />
                            </Tooltip>
                          </Box>
                        }
                      />
                    </TempUserToolTip>
                  </Box>
                  {showIsRemoteAlert && (
                    <Box mt={26}>
                      <Alert variant="info">
                        <FDBText
                          variant="bodyTight"
                          i18n="team.employee_profile.payroll_ee_information.alerts.is_remote"
                        />
                      </Alert>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )
        : (showIsRemoteReadMode &&
            isRemoteTeamMemberReadModeValue === 'success' && (
              <Box row mt={16}>
                <Icon iconName="Info" color="info300" size="large" mr={8} />
                <FDBText
                  variant="body"
                  // eslint-disable-next-line max-len
                  i18n={`team.employee_profile.payroll_ee_information.is_remote.read_mode.${isRemoteTeamMemberReadModeValue}`}
                  i18nProps={{
                    name: user.get('legal_name') || user.get('name'),
                  }}
                  asHTML
                />
              </Box>
            )) ||
          (showIsRemoteReadMode &&
            isRemoteTeamMemberReadModeValue === 'error' && (
              <Box row mt={16}>
                <Alert variant="error">
                  <FDBText
                    variant="bodyTight"
                    // eslint-disable-next-line max-len
                    i18n={`team.employee_profile.payroll_ee_information.is_remote.read_mode.${isRemoteTeamMemberReadModeValue}`}
                    i18nProps={{
                      name: user.get('legal_name') || user.get('name'),
                    }}
                    asHTML
                  />
                </Alert>
              </Box>
            ))}
    </Box>
  );

  const renderBusinessName = editMode => (
    <Box mt12 className={cxEl('business-name')} vcenter>
      <Box w={mobile ? '40%' : '20%'}>
        <TeamSectionTitle i18n="team.employee_profile.employee_information.business_name" />
      </Box>
      {editMode ? (
        <Box
          w={mobile ? '60%' : '80%'}
          className={cxEl('contractor-info-business-name')}
        >
          <Box w={mobile ? '100%' : '48%'}>
            <TempUserToolTip
              name={user.get('first_name')}
              enabled={isTooltipEnabled}
            >
              <FieldRenderer
                editMode
                name="contractor_info_business_name"
                component="TextField"
                theme="bordered"
                debounced={false}
                placeholder={toI18n(
                  'team.employee_profile.employee_information.placeholders.business_name'
                )}
                disabled={isFieldDisabled}
              />
            </TempUserToolTip>
          </Box>
        </Box>
      ) : (
        <Box w={mobile ? '60%' : '80%'}>
          <RequiredAttribute
            user={user}
            targetObject={user}
            attribute="contractor_info_business_name"
          >
            <Text navy>{user.get('contractor_info_business_name')}</Text>
          </RequiredAttribute>
        </Box>
      )}
    </Box>
  );

  const renderDateOfBirth = (editMode, errors) => (
    <Box row mt12 className={cxEl('date-of-birth-row')} vcenter>
      <Box w={mobile ? '40%' : '20%'}>
        <TeamSectionTitle i18n="team.employee_profile.employee_information.date_of_birth" />
      </Box>
      {editMode ? (
        <Box className={cxEl('date-of-birth')} w={mobile ? '60%' : '80%'}>
          <Box w={mobile ? '100%' : '48%'}>
            <TempUserToolTip
              name={user.get('first_name')}
              enabled={
                disableEdits &&
                !showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert
              }
            >
              <FieldRenderer
                editMode
                name="date_of_birth"
                component="TextField"
                theme="bordered"
                mask="date"
                debounced={false}
                placeholder={toI18n(
                  'team.employee_profile.employee_information.placeholders.date_of_birth'
                )}
                disabled={isFieldDisabled}
                errorBorder={errors.date_of_birth}
              />
            </TempUserToolTip>
          </Box>
        </Box>
      ) : (
        <Box w={mobile ? '60%' : '80%'}>
          <RequiredAttribute
            user={user}
            targetObject={user}
            attribute="date_of_birth"
          >
            <Text navy>{user.get('date_of_birth')}</Text>
          </RequiredAttribute>
        </Box>
      )}
    </Box>
  );

  const renderSSN = (editMode, errors, setFieldValue, values) => (
    <Box row mt12 className={cxEl('ssn')} vcenter>
      <Box w={mobile ? '40%' : '20%'}>
        <TeamSectionTitle i18n="team.employee_profile.employee_information.ssn" />
      </Box>
      {editMode ? (
        <Box w={mobile ? '60%' : '80%'} className={cxEl('decrypted-ssn')}>
          <Box w={mobile ? '100%' : '48%'} relative>
            <TempUserToolTip
              name={user.get('first_name')}
              enabled={isTooltipEnabled}
            >
              <FieldRenderer
                editMode
                onBlur={partial(handleOnBlurSsn, setFieldValue, values)}
                onChange={handleSSNChange}
                name="decrypted_ssn"
                component="TextField"
                theme="bordered"
                mask="ssn"
                type={isSSNVisible ? 'text' : 'password'}
                debounced={false}
                placeholder={
                  user.get('decrypted_ssn') ||
                  toI18n(
                    'team.employee_profile.employee_information.placeholders.ssn'
                  )
                }
                disabled={
                  isFieldDisabled ||
                  (!currentUserIsPayrollAdmin && !!user.get('decrypted_ssn'))
                }
                errorBorder={errors.encrypted_ssn}
              />
            </TempUserToolTip>
            <Box
              absolute
              style={{
                right: '12px',
                top: '8px',
              }}
            >
              <button
                type="button"
                onClick={handleClickSSNVisible}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Icon
                  iconName={isSSNVisible ? 'Hide' : 'Show'}
                  size="medium"
                  color="purple500"
                />
              </button>
            </Box>
          </Box>

          {isSSNChanged && hasPayrollEnabled && !!user.get('decrypted_ssn') && (
            <Box mt4>
              <Warning
                orangeDark
                i18n="team.employee_profile.employee_information.warnings.ssn"
              />
            </Box>
          )}
        </Box>
      ) : (
        <Box w={mobile ? '60%' : '80%'}>
          <RequiredAttribute
            user={user}
            targetObject={user}
            attribute="decrypted_ssn"
          >
            <Text navy>{user.get('decrypted_ssn')}</Text>
          </RequiredAttribute>
        </Box>
      )}
    </Box>
  );

  const renderEin = (editMode, errors) => (
    <Box row mt12 className={cxEl('ein')} vcenter>
      <Box w={mobile ? '40%' : '20%'}>
        <TeamSectionTitle i18n="team.employee_profile.employee_information.ein" />
      </Box>
      {editMode ? (
        <Box
          w={mobile ? '60%' : '80%'}
          className={cxEl('contractor-info-decrypted-ein')}
        >
          <Box w={mobile ? '100%' : '48%'}>
            <TempUserToolTip
              name={user.get('first_name')}
              enabled={isTooltipEnabled}
            >
              <FieldRenderer
                editMode
                name="contractor_info_decrypted_ein"
                component="TextField"
                theme="bordered"
                mask="ein"
                debounced={false}
                placeholder={toI18n(
                  'team.employee_profile.employee_information.placeholders.ein'
                )}
                disabled={
                  isFieldDisabled ||
                  (!currentUserIsPayrollAdmin &&
                    !!user.get('contractor_info_decrypted_ein'))
                }
                errorBorder={errors.contractor_info_decrypted_ein}
              />
            </TempUserToolTip>
          </Box>
        </Box>
      ) : (
        <Box w={mobile ? '60%' : '80%'}>
          <RequiredAttribute
            user={user}
            targetObject={user}
            attribute="contractor_info_decrypted_ein"
          >
            <Text navy>{user.get('contractor_info_decrypted_ein')}</Text>
          </RequiredAttribute>
        </Box>
      )}
    </Box>
  );

  const checkAddressError = useCallback(
    () => user.getIn(['address_errors', 'error']) || fromJS(suggestedAddress),
    [user, suggestedAddress]
  );

  const checkAddressErrorMessage = useCallback(
    errorKey => {
      const errorMessage = checkAddressError()?.get('message');

      if (errorMessage) {
        if (errorMessage === CHECK_ADDRESS_ERROR.messages.default) {
          return CHECK_ADDRESS_ERROR.messages.invalidAddress;
        }

        const message = errorMessage
          ?.split(CHECK_ADDRESS_ERROR.default_split_key)[1]
          ?.replace(
            CHECK_ADDRESS_ERROR.transformation_regex,
            CHECK_ADDRESS_ERROR.default_separator
          )
          ?.toLowerCase();

        if (errorKey) {
          return message === errorKey ? message : null;
        }

        return message;
      }
    },
    [checkAddressError]
  );

  const checkAddressSuggestedAlternative = useCallback(
    () => checkAddressError()?.get('suggested_alternative'),
    [checkAddressError]
  );

  const [isCustomValueApplied, setIsCustomValueApplied] = useState(false);

  const renderAddress = (editMode, errors) => (
    <Box className={cxEl('address')} mt12>
      <Box row vcenter>
        {editMode ? (
          <>
            <Box w={mobile ? '40%' : '20%'}>
              <TeamSectionTitle i18n="team.employee_profile.payroll_ee_information.address_1" />
            </Box>
            <Box w={mobile ? '60%' : '80%'} className={cxEl('address-1')}>
              <Box w={mobile ? '100%' : '48%'}>
                <TempUserToolTip
                  name={user.get('first_name')}
                  enabled={isTooltipEnabled}
                >
                  <FieldRenderer
                    editMode
                    name="address_1"
                    component="TextField"
                    theme="bordered"
                    debounced={false}
                    errorBorder={
                      !!checkAddressErrorMessage(
                        CHECK_ADDRESS_ERROR.messages.unknownStreet
                      ) || errors.address_1
                    }
                    placeholder={toI18n(
                      'team.employee_profile.employee_information.placeholders.address_1'
                    )}
                    isCustomValueApplied={isCustomValueApplied}
                    setIsCustomValueApplied={setIsCustomValueApplied}
                    customValue={checkAddressSuggestedAlternative()?.get(
                      'line1'
                    )}
                    disabled={isFieldDisabled}
                  />
                </TempUserToolTip>
                <CheckAddressError
                  payrollAddressValidationRollout={
                    payrollAddressValidationRollout
                  }
                  editMode={editMode}
                  checkAddressErrorMessage={partial(
                    checkAddressErrorMessage,
                    CHECK_ADDRESS_ERROR.messages.unknownStreet
                  )}
                />
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box w={mobile ? '40%' : '20%'}>
              <TeamSectionTitle i18n="team.employee_profile.payroll_ee_information.address" />
            </Box>
            <Box w={mobile ? '60%' : '80%'}>
              <RequiredAttribute
                user={user}
                targetObject={user}
                attribute="address_1"
              >
                <Text navy>{`${user.get('address_1') || ''}${renderComma(
                  user.get('address_2')
                )} ${user.get('address_2') || ''}`}</Text>
                {checkAddressErrorMessage() && (
                  <Box ml={8} inline>
                    <Pill variant="warning">
                      {toI18n(
                        'team.employee_profile.payroll_ee_information.invalid_address'
                      )}
                    </Pill>
                  </Box>
                )}
              </RequiredAttribute>
            </Box>
          </>
        )}
      </Box>
      <Box row mt12 vcenter>
        {editMode ? (
          <>
            <Box w={mobile ? '40%' : '20%'}>
              <TeamSectionTitle i18n="team.employee_profile.payroll_ee_information.address_2" />
            </Box>
            <Box w={mobile ? '60%' : '80%'} className={cxEl('address-2')}>
              <Box w={mobile ? '100%' : '48%'}>
                <TempUserToolTip
                  name={user.get('first_name')}
                  enabled={isTooltipEnabled}
                >
                  <FieldRenderer
                    editMode
                    name="address_2"
                    component="TextField"
                    theme="bordered"
                    debounced={false}
                    disabled={isFieldDisabled}
                    placeholder={toI18n(
                      'team.employee_profile.employee_information.placeholders.address_2'
                    )}
                  />
                </TempUserToolTip>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box w={mobile ? '40%' : '20%'} />
            <Box w={mobile ? '60%' : '80%'}>
              <RequiredAttribute
                user={user}
                targetObject={user}
                attribute="city"
              >
                <Box column>
                  <Text navy>{`${user.get('city') || ''}${renderComma(
                    user.get('city')
                  )} ${user.get('state') || ''} ${
                    user.get('zip_code') || ''
                  }`}</Text>
                  <CheckAddressError
                    payrollAddressValidationRollout={
                      payrollAddressValidationRollout
                    }
                    editMode={editMode}
                    checkAddressErrorMessage={checkAddressErrorMessage}
                  />
                </Box>
              </RequiredAttribute>
            </Box>
          </>
        )}
      </Box>
      {editMode && (
        <>
          <Box row mt12 vcenter>
            <Box w={mobile ? '40%' : '20%'}>
              <TeamSectionTitle i18n="team.employee_profile.payroll_ee_information.city" />
            </Box>
            <Box w={mobile ? '60%' : '80%'} className={cxEl('city')}>
              <Box w={mobile ? '100%' : '48%'}>
                <TempUserToolTip
                  name={user.get('first_name')}
                  enabled={isTooltipEnabled}
                >
                  <FieldRenderer
                    editMode
                    name="city"
                    component="TextField"
                    theme="bordered"
                    debounced={false}
                    errorBorder={
                      !!checkAddressErrorMessage(
                        CHECK_ADDRESS_ERROR.messages.incorrectCity
                      ) || errors.city
                    }
                    isCustomValueApplied={isCustomValueApplied}
                    setIsCustomValueApplied={setIsCustomValueApplied}
                    customValue={checkAddressSuggestedAlternative()?.get(
                      'city'
                    )}
                    disabled={isFieldDisabled}
                    placeholder={toI18n(
                      'team.employee_profile.employee_information.placeholders.city'
                    )}
                  />
                </TempUserToolTip>
                <CheckAddressError
                  payrollAddressValidationRollout={
                    payrollAddressValidationRollout
                  }
                  editMode={editMode}
                  checkAddressErrorMessage={partial(
                    checkAddressErrorMessage,
                    CHECK_ADDRESS_ERROR.messages.incorrectCity
                  )}
                />
              </Box>
            </Box>
          </Box>
          <Box row mt12 vcenter>
            <Box w={mobile ? '40%' : '20%'}>
              <TeamSectionTitle i18n="team.employee_profile.payroll_ee_information.state_zip" />
            </Box>
            <Box w={mobile ? '60%' : '80%'} row spacebetween>
              <Box w="52%" className={cxEl('state')}>
                <Box>
                  <TempUserToolTip
                    name={user.get('first_name')}
                    enabled={
                      disableEdits &&
                      !showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert
                    }
                  >
                    <FieldRenderer
                      editMode
                      name="state"
                      component="SelectInput"
                      theme="bordered"
                      options={stateOptions}
                      clearable={false}
                      debounced={false}
                      placeholder={toI18n(
                        'team.employee_profile.employee_information.placeholders.state'
                      )}
                      isCustomValueApplied={isCustomValueApplied}
                      setIsCustomValueApplied={setIsCustomValueApplied}
                      customValue={checkAddressSuggestedAlternative()?.get(
                        'state'
                      )}
                      disabled={isFieldDisabled}
                      style={
                        checkAddressErrorMessage(
                          CHECK_ADDRESS_ERROR.messages.incorrectState
                        )
                          ? { border: 'solid red', borderWidth: 'thin' }
                          : null
                      }
                      errorBorder={errors.state}
                    />
                  </TempUserToolTip>
                  <CheckAddressError
                    payrollAddressValidationRollout={
                      payrollAddressValidationRollout
                    }
                    editMode={editMode}
                    checkAddressErrorMessage={partial(
                      checkAddressErrorMessage,
                      CHECK_ADDRESS_ERROR.messages.incorrectState
                    )}
                  />
                </Box>
              </Box>
              <Box w="44%">
                <Box className={cxEl('zip-code')}>
                  <TempUserToolTip
                    name={user.get('first_name')}
                    enabled={
                      disableEdits &&
                      !showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert
                    }
                  >
                    <FieldRenderer
                      editMode
                      name="zip_code"
                      component="TextField"
                      theme="bordered"
                      debounced={false}
                      errorBorder={
                        !!checkAddressErrorMessage(
                          CHECK_ADDRESS_ERROR.messages.incorrectPostalCode
                        ) || errors.zip_code
                      }
                      mask={zipMask()}
                      placeholder={toI18n(
                        'team.employee_profile.employee_information.placeholders.zip_code'
                      )}
                      isCustomValueApplied={isCustomValueApplied}
                      setIsCustomValueApplied={setIsCustomValueApplied}
                      customValue={checkAddressSuggestedAlternative()?.get(
                        'postal_code'
                      )}
                      disabled={isFieldDisabled}
                    />
                  </TempUserToolTip>
                  <CheckAddressError
                    payrollAddressValidationRollout={
                      payrollAddressValidationRollout
                    }
                    editMode={editMode}
                    checkAddressErrorMessage={partial(
                      checkAddressErrorMessage,
                      CHECK_ADDRESS_ERROR.messages.incorrectPostalCode
                    )}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
      <CheckAddressSuggestion
        editMode={editMode}
        mobile={mobile}
        checkAddressErrorMessage={checkAddressErrorMessage}
        checkAddressSuggestedAlternative={checkAddressSuggestedAlternative}
        onApplySuggestedAddress={partial(setIsCustomValueApplied, true)}
      />
    </Box>
  );

  const widgetTitle = useMemo(() => {
    if (teamDrawerMode) {
      return;
    }
    return 'team.employee_profile.payroll_ee_information.title';
  }, [teamDrawerMode]);

  return (
    <EditableSection
      onSaveCompleted={onSaveCompleted}
      noBorder={teamDrawerMode}
      noPadding={teamDrawerMode}
      className={cx()}
      titleI18n={widgetTitle}
      onSave={handleSave}
      onValidate={handleValidate}
      onError={handleOnError}
      updateUserInfo={updateUserInfo}
      initialValues={initialValues}
      cantEdit={teamDrawerMode}
      alertVariant={isSSNInvalid && 'warning'}
      alertI18n={
        isSSNInvalid &&
        'team.employee_profile.payroll_ee_information.ssn_invalid_alert'
      }
      editButtonDisabled={
        !isGodModing && employeeInformationEditButton.disabled
      }
      editButtonContent={
        isGodModing ? null : employeeInformationEditButton.content
      }
      cxEl={cxEl}
      initialErrors={getInitialErrors(
        initialValues,
        user.get('tax_classification')
      )}
      initEditMode={initEditMode || openByDefault}
      showCancelButton={!teamDrawerMode}
    >
      {({ editMode, errors, setFieldValue, values }) => (
        <EmployeeInformationForm
          user={user}
          editMode={editMode}
          errors={errors}
          setFieldValue={setFieldValue}
          values={values}
          handleShowRemoteTeamMemberAlert={handleShowRemoteTeamMemberAlert}
          renderLegalName={renderLegalName}
          renderDateOfBirth={renderDateOfBirth}
          renderBusinessName={renderBusinessName}
          renderEin={renderEin}
          renderSSN={renderSSN}
          renderAddress={renderAddress}
          renderIsRemoteCheckBox={renderIsRemoteCheckBox}
          showIsRemoteField={showIsRemoteField}
          disableEdits={disableEdits}
          showEmployeeWillBeNotifiedAlert={showEmployeeWillBeNotifiedAlert}
          showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert={
            showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert
          }
        />
      )}
    </EditableSection>
  );
};

EmployeeInformationWidget.propTypes = {
  user: ImmutablePropTypes.map.isRequired,
  updateUserInfo: PropTypes.func.isRequired,
  mobile: PropTypes.bool.isRequired,
  stateOptions: ImmutablePropTypes.list.isRequired,
  currentCompany: PropTypes.object.isRequired,
  hasPayrollEnabled: PropTypes.bool.isRequired,
  currentUserIsPayrollAdmin: PropTypes.bool.isRequired,
  teamDrawerMode: PropTypes.bool,
  lazyFetchUser: PropTypes.func,
  disableEdits: PropTypes.bool,
  initEditMode: PropTypes.bool,
  onSaveCompleted: PropTypes.func,
};

export default connect((state, props) => ({
  payrollAddressValidationRollout: getPayrollAddressValidationRollout(state),
  currentCompany: getCurrentCompany(state, props),
  hasPayrollEnabled: getHasPayrollEnabled(state),
  currentUserIsPayrollAdmin: getCurrentUserIsPayrollAdmin(state),
}))(EmployeeInformationWidget);
